import ApolloClient from "apollo-client";
import { InMemoryCache } from "apollo-cache-inmemory";
import { createUploadLink } from "apollo-upload-client";
import * as AbsintheSocket from "@absinthe/socket";
import { createAbsintheSocketLink } from "@absinthe/socket-apollo-link";
import { Socket as PhoenixSocket } from "phoenix";
import { setContext } from "apollo-link-context";
import { onError } from "apollo-link-error";
import Cookies from "universal-cookie";
import { split } from "apollo-link";
import { getMainDefinition } from "apollo-utilities";
import { TOKEN_KEY, forceLogout } from "../utils/auth.js";
import monitorSocket from "./socketMonitor.js";
import { installDebugHandle, isVerbose, phoenixLogger } from "./socketDebug.js";
import { CLIENT_VERSION } from "./clientVersion.js";

const ORIGIN_WS = process.env.NODE_ENV === "production"
  ? `${window.location.protocol.includes("https") ? "wss" : "ws"}://${window.location.hostname}`
  : "ws://localhost:4000";

const ORIGIN_HTTP = process.env.NODE_ENV === "production"
  ? `${window.location.protocol}//${window.location.hostname}`
  : "http://localhost:4000";

const WEBSOCKET_ENDPOINT = `${ORIGIN_WS}/graphql`;
const HTTP_ENDPOINT = `${ORIGIN_HTTP}/graphql`;

// phoenix.js hands the socket timeout down to the channel, so it is also the join
// and push timeout for every subscription. The previous 15 minutes meant a single
// lost join/subscribe reply left the panel silently stale for 15 minutes before
// anything was retried.
const SOCKET_TIMEOUT = 30000;
const HEARTBEAT_INTERVAL = 15000;
const RECONNECT_BACKOFF = [1000, 2000, 4000, 8000, 15000, 30000];

// Jitter keeps every browser on the plant from retrying in lockstep after the
// device restarts.
const withJitter = ms => Math.round(ms * (0.75 + Math.random() * 0.5));

const reconnectAfterMs = (tries) => {
  const step = RECONNECT_BACKOFF[tries - 1]
    || RECONNECT_BACKOFF[RECONNECT_BACKOFF.length - 1];

  return withJitter(step);
};

// Subscription errors are dropped on the floor unless a handler is given: the
// absinthe socket only notifies observers that define `onError`, and none of them
// do by default. That hid the two failures that look identical to the operator —
// the connection closing, and the subscription being refused ("Não autorizado")
// after the token expired, which never recovers without a fresh login.
// A dead absinthe channel makes every notifier error at once, and there can be
// dozens of them. Logging each error unthrottled floods the console (observed:
// 40k+ warnings) until the main thread saturates and the tab can no longer even
// be closed. Collapse bursts into a single line with a suppressed count so the
// signal survives without the flood.
const LOG_THROTTLE_MS = 2000;
let lastSubscriptionLogAt = 0;
let suppressedSubscriptionErrors = 0;

const logSubscriptionError = (error) => {
  const message = error && error.message ? error.message : error;
  const now = Date.now();

  if (now - lastSubscriptionLogAt < LOG_THROTTLE_MS) {
    suppressedSubscriptionErrors += 1;
    return;
  }

  lastSubscriptionLogAt = now;
  const suppressed = suppressedSubscriptionErrors;
  suppressedSubscriptionErrors = 0;

  // eslint-disable-next-line no-console
  console.warn(
    "[graphql-subscription]",
    message,
    suppressed > 0 ? `(+${suppressed} suprimidos)` : "",
  );
};

// Only one absinthe socket may be live at a time. A new one is created on login,
// token refresh, or a remounted provider (and `withAuthentication` can create two
// at once under a getCurrentUser race). Each orphaned socket keeps its own
// visibilitychange watchdog and, on every tab focus, re-pushes all of its
// subscriptions — which pile duplicate registrations on the server (measured:
// thousands of copies of a single subscription on one socket) until CPU spikes.
// Retiring the previous socket here makes the whole stack single-socket regardless
// of how many times the client is rebuilt.
let activeSocketSession = null;

const retirePreviousSocket = () => {
  if (!activeSocketSession) return;

  const { socket, monitor } = activeSocketSession;
  activeSocketSession = null;

  try {
    monitor.stop();
  } catch (error) {
    // no-op: monitor already stopped
  }

  try {
    // Permanent teardown (not the watchdog's reconnect path): we are throwing the
    // whole client away, so we want the socket dead and its server-side
    // subscriptions gone, not preserved for a rejoin.
    socket.disconnect();
  } catch (error) {
    // no-op: socket already disconnected
  }
};

// Subscriptions ride a single socket. A flapping reconnect can leave them dead
// (notifiers dropped, control channel not re-joined) while the socket still reports
// "open"; only a fresh socket recovers it. socketMonitor detects the flapping and
// calls back here, and withAuthentication rebuilds the subscription client.
const rebuildListeners = new Set();

const onSubscriptionsNeedRebuild = (listener) => {
  rebuildListeners.add(listener);
  return () => rebuildListeners.delete(listener);
};

const notifySubscriptionsNeedRebuild = () => {
  rebuildListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      // no-op: one listener throwing must not block the others
    }
  });
};

// Lets a caller outside the socket monitor (the ErrorBoundary, after a render
// crash) ask for the same subscription-client rebuild a flapping reconnect
// triggers — a fresh socket re-joins and re-subscribes, which is what closing
// and reopening the tab achieves without losing the page.
const requestSubscriptionRebuild = () => notifySubscriptionsNeedRebuild();

const createAbsintheLink = (token) => {
  retirePreviousSocket();

  const socket = new PhoenixSocket(
    WEBSOCKET_ENDPOINT,
    {
      params: { Bearer: token, client_version: CLIENT_VERSION },
      timeout: SOCKET_TIMEOUT,
      heartbeatIntervalMs: HEARTBEAT_INTERVAL,
      reconnectAfterMs,
      // Opt-in via `__socket.verbose()`; null keeps phoenix quiet.
      logger: isVerbose() ? phoenixLogger : null,
    },
  );

  // On socket (re)connection, resync the HTTP-cache-backed queries so that any
  // realtime updates missed while the websocket was offline are recovered.
  // This is what lets us drop the periodic polling: subscriptions keep state
  // live while connected, and this handler heals drift after a reconnect.
  // The first open (initial connect) is skipped because those queries are
  // already being fetched on mount.
  let isFirstOpen = true;
  socket.onOpen(() => {
    if (isFirstOpen) {
      isFirstOpen = false;
      return;
    }

    // Returns a promise, so a rejected refetch cannot be caught by try/catch —
    // one bad query used to surface as an unhandled rejection in the console.
    try {
      const resync = httpClient.reFetchObservableQueries();

      if (resync && resync.catch) {
        resync.catch(error => logSubscriptionError(error));
      }
    } catch (error) {
      // no-op: no observable queries to resync yet
    }
  });

  // The socket's own heartbeat cannot detect a connection that died while the tab
  // was throttled or frozen, which is what made the panel need a page reload. The
  // monitor also ends the session when the handshake is being refused because the
  // token expired, which no amount of retrying can fix.
  const monitor = monitorSocket(socket, {
    token,
    apiOrigin: ORIGIN_HTTP,
    onFlappingRecovered: notifySubscriptionsNeedRebuild,
  });
  const absintheSocket = AbsintheSocket.create(socket);

  activeSocketSession = { socket, monitor };

  // Exposes `window.__socket` for bench diagnosis. Read-only snapshot plus
  // explicitly-invoked actions; nothing runs on its own.
  installDebugHandle({ socket, absintheSocket, monitor });

  return createAbsintheSocketLink(absintheSocket, logSubscriptionError);
};

// A revoked token (e.g. the user's password was changed elsewhere) resolves to
// no current_user, so protected operations come back "Não autenticado". Catch it
// anywhere in the app and end the session instead of leaving a dead one running.
const isUnauthenticated = message => typeof message === "string"
  && message.toLowerCase().includes("não autenticado");

const authErrorLink = onError(({ graphQLErrors, networkError }) => {
  const unauthenticated = (graphQLErrors || []).some(error => isUnauthenticated(error.message))
    || (networkError && networkError.statusCode === 401);

  if (unauthenticated) {
    forceLogout();
  }
});

const httpAuthLink = setContext((_, { headers }) => {
  const cookies = new Cookies();
  const token = cookies.get(TOKEN_KEY, { path: "/" });

  return {
    headers: {
      ...headers,
      authorization: `Bearer ${token}`,
    },
  };
});

const httpLink = createUploadLink({
  uri: HTTP_ENDPOINT,
  credentials: "include",
});

const authenticatedLink = authErrorLink.concat(httpAuthLink).concat(httpLink);

const defaultOptions = {
  watchQuery: {
    fetchPolicy: "cache-and-network",
  },
};

const httpClient = new ApolloClient({
  link: authenticatedLink,
  cache: new InMemoryCache(),
  defaultOptions,
});

const createSplitLink = token => split(
  ({ query }) => {
    const { kind, operation } = getMainDefinition(query);
    return kind === "OperationDefinition" && operation === "subscription";
  },
  createAbsintheLink(token),
  authenticatedLink,
);

const createSplitClient = token => new ApolloClient({
  link: createSplitLink(token),
  cache: new InMemoryCache(),
  defaultOptions,
});

export {
  httpClient,
  createSplitClient,
  onSubscriptionsNeedRebuild,
  requestSubscriptionRebuild,
};
